import { supabase } from '../../lib/supabase'
import type { CoverageLookups, CoverageViewRow } from './coverageReport'

type NamedRow = { id: string; name: string }

// A date range of the `coverage` view. Row-level security limits it to the
// caller's own teams for a supervisor and opens every team to leadership and
// administrators, so no team filter is applied here.
export async function getCoverageRows(from: string, to: string): Promise<CoverageViewRow[]> {
  const { data, error } = await supabase
    .from('coverage')
    .select(
      'date, team_id, shift_template_id, assignment_id, is_shift_total, total_assigned, total_available, total_on_duty, total_on_leave, total_detailed_out, planned_overtime_hours, required_staffing, variance, has_unfilled_assignment, has_qualification_shortage, coverage_state',
    )
    .gte('date', from)
    .lte('date', to)
    .order('date', { ascending: true })
  if (error) throw new Error('The coverage report could not be loaded.')
  return (data ?? []) as unknown as CoverageViewRow[]
}

async function namesFrom(table: 'shift_templates' | 'assignments'): Promise<Map<string, string>> {
  const { data, error } = await supabase.from(table).select('id, name')
  if (error) throw new Error('The coverage report could not be loaded.')
  return new Map(((data ?? []) as NamedRow[]).map((row) => [row.id, row.name]))
}

type TeamRow = NamedRow & { supervisor_id: string | null }

export type TeamLookups = Pick<CoverageLookups, 'teams' | 'supervisors'>

// The team names, and the account that supervises each.
export async function getTeamLookups(): Promise<TeamLookups> {
  const { data, error } = await supabase.from('teams').select('id, name, supervisor_id')
  if (error) throw new Error('The report could not be loaded.')
  const teamRows = (data ?? []) as TeamRow[]
  return {
    teams: new Map(teamRows.map((row) => [row.id, row.name])),
    supervisors: new Map(teamRows.flatMap((row) => (row.supervisor_id ? [[row.id, row.supervisor_id] as [string, string]] : []))),
  }
}

export async function getCoverageLookups(): Promise<CoverageLookups> {
  const [teamLookups, shifts, assignments] = await Promise.all([getTeamLookups(), namesFrom('shift_templates'), namesFrom('assignments')])
  return { ...teamLookups, shifts, assignments }
}
